import { Action } from '@wix/component-protocol/schema';
import {
  EditActionDefinition,
  SystemEditActionFactory,
} from '@wix/editor-platform-host-integration-apis';
import { EditActionsStore } from '../editActionsStore';
import {
  ActionFactoryWithPlatformContext,
  EditAction,
  IEditActionBuilder,
} from '../types';
import { platformSystemActions } from './systemActions';
import { getActionId } from '../utils/getActionId';

export class SystemActionBuilder extends IEditActionBuilder<string> {
  #build(
    actionName: string,
    actionDefinition: Action,
  ): EditActionDefinition<string> | null {
    const factory = EditActionsStore.getSystemAction(actionName);

    if (!factory) {
      return null;
    }

    if (this.#isPlatformContributed(actionName, factory)) {
      return factory(
        this.ctx.createExtendedPlatformContext(actionName, actionDefinition),
      );
    }

    return (factory as SystemEditActionFactory)(
      this.ctx.createSystemActionContext(actionName, actionDefinition),
    );
  }

  build(
    actionName: string,
    actionDefinition: Action,
  ): EditActionDefinition<string> | null {
    const contributedEditAction = this.#build(actionName, actionDefinition);

    if (!contributedEditAction) {
      return null;
    }

    return this.#applyUIOverrides(actionName, {
      ...contributedEditAction,
      actionId:
        contributedEditAction.actionId || getActionId('system', actionName),
    });
  }

  #isPlatformContributed(
    actionName: string,
    _factory: EditAction,
  ): _factory is ActionFactoryWithPlatformContext {
    return Object.keys(platformSystemActions).includes(actionName);
  }

  #applyUIOverrides(
    actionName: string,
    resolvedAction: EditActionDefinition<string>,
  ): EditActionDefinition<string> {
    const uiOverride = EditActionsStore.getActionUIOverride(actionName) || {};

    if (Object.keys(uiOverride).length === 0) {
      return resolvedAction;
    }

    return {
      ...resolvedAction,
      ...uiOverride,
    };
  }
}
